import { useGlobalSearchContext } from "@/providers/global-search/GlobalSearchContext";
import SearchResultsLegislationFilters from "../search-result/SearchResultsLegislationFilters";
import { SearchTag } from "@/components/data-table/search-tag";
import { Star, Trash } from "lucide-react";
import { Search } from "@/models/search/Search";
import { isSourceSearch } from "@/lib/search/sourceSearch";
import { getTabsName } from "@/pages/global-search/utils/searchUtils";
import { cn } from "@/lib/utils";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";

interface GlobalSearchSearchItemProps {
  search: Search;
  isFavorite: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onToggleFavorite: () => void;
  isDeleteBusy: boolean;
  isFavoriteBusy: boolean;
}

const GlobalSearchSearchItem = ({
  search,
  isFavorite,
  onSelect,
  onDelete,
  onToggleFavorite,
  isDeleteBusy,
  isFavoriteBusy,
}: GlobalSearchSearchItemProps) => {
  const { leveledFilters } = useGlobalSearchContext();

  // Determine what to show as the main search display
  const hasSearchTags = search.searchTags && search.searchTags.length > 0;
  const hasSearchTerm = search.searchTerm && search.searchTerm.trim() !== "";
  const hasAnySearchText = hasSearchTags || hasSearchTerm;

  return (
    <div
      className="hover:bg-accent hover:text-accent-foreground p-2 px-3 rounded-sm cursor-pointer"
      onClick={onSelect}
    >
      <div className="flex items-center justify-between w-full">
        <div className={cn("flex-1 flex flex-col gap-1 min-w-0")}>
          {/* Search tags and/or search term */}
          <div className="flex items-center flex-wrap gap-1">
            {hasSearchTags &&
              search.searchTags!.map((tag, index) => (
                <SearchTag key={index} tag={tag} variant="inline" />
              ))}
            {hasSearchTerm && (
              <span
                className="font-medium text-sm truncate"
                title={search.searchTerm}
              >
                {search.searchTerm}
              </span>
            )}
            {!hasAnySearchText && (
              <span className="text-sm text-muted-foreground italic">
                Ingen søgnings tekst
              </span>
            )}
          </div>
          {/* Filters */}
          <div className="text-muted-foreground text-xs flex items-center overflow-hidden">
            {/* A source search reruns on the Kilder page, so say where it goes. */}
            {isSourceSearch(search) && (
              <span className="mr-1.5 shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-[10px]">
                {getTabsName(search.tab)}
              </span>
            )}
            {search.filters && Object.keys(search.filters).length > 0 && (
              <div className="flex items-center shrink min-w-0">
                <SearchResultsLegislationFilters
                  legislationFilters={search.filters}
                  leveledFilters={leveledFilters}
                />
              </div>
            )}
          </div>
        </div>

        {search.id && (
          <div className="flex items-center gap-1 ml-2 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="hover:bg-muted hover:text-destructive h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={isDeleteBusy}
            >
              {isDeleteBusy ? (
                <Spinner className="w-3.5 h-3.5" />
              ) : (
                <Trash className="w-3.5 h-3.5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="hover:bg-muted h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite();
              }}
              disabled={isFavoriteBusy}
            >
              {isFavoriteBusy ? (
                <Spinner className="w-3.5 h-3.5" />
              ) : (
                <Star
                  className={cn(
                    "w-3.5 h-3.5",
                    isFavorite && "text-warning fill-warning"
                  )}
                />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearchSearchItem;
